/**
 * Hey! Welcome to @beyondbreath/ui Button
 *
 * Button component is used to trigger an action or event, such as
 * submitting a form, opening a Dialog, canceling an action, or
 * performing a delete operation
 *
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices-1.2/#button
 */
<template>
  <ULink
    :type="type"
    :variant="variant"
    :disabled="isDisabled || isLoading"
    v-bind="$attrs"
    :class="[
      classes,
      { 'rounded-full': circle }
    ]"
    class="relative inline-flex items-center justify-center hover:scale-[101%] hover:duration-500 hover:ease-in-out appearance-none cursor-pointer"
  >
    <!--Pulsing background-->
    <div
      v-show="pulseEffectEnabled"
      :class="pulseClasses"
      class="absolute top-0 left-0 w-full h-full transition duration-300 rounded-lg animate-pulse-ring ring-4 group-hover:ring-0"
    />

    <!--Main Label-->
    <slot>
      <span
        v-if="label"
        :class="[truncate ? 'text-left break-all line-clamp-1' : '']"
      >
        {{ label }}
      </span>
    </slot>

    <!--Loader-->
    <svg
      v-if="isLoading"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      class="w-4 h-4 ml-2 -mr-1 animate-spin"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        fill="currentColor"
        fill-rule="evenodd"
        d="M12 19a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm0 3c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
        clip-rule="evenodd"
        opacity=".2"
      />
      <path
        fill="currentColor"
        d="M2 12C2 6.477 6.477 2 12 2v3a7 7 0 0 0-7 7H2Z"
      />
    </svg>
  </ULink>
</template>

<script setup>
import ULink from '../Link/Link.vue';
import { computed } from 'vue'

defineOptions({
  inheritAttrs: false
})

const props = defineProps({
  type: {
    type: String,
    default: null,
  },
  variant: {
    type: String,
    default: 'primary',
  },
  size: {
    type: String,
    default: 'normal',
  },
  circle: {
    type: Boolean,
    default: false
  },
  pulseEffectEnabled: {
    type: Boolean,
    default: false
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  isDisabled: {
    type: Boolean,
    default: false
  }
})

const variants = [
  { name: 'primary', classes: 'border-none bg-primary text-dark-900 hover:bg-primary-dark focus:outline-none focus:ring-4 focus:ring-primary focus:ring-opacity-40', pulse_classes: 'ring-primary/50' },
  { name: 'primary-dark-text', classes: 'border-none bg-primary text-dark-900 hover:bg-primary-dark focus:outline-none focus:ring-4 focus:ring-primary focus:ring-opacity-40', pulse_classes: 'ring-primary/50' },
  { name: 'primary-outline', classes: 'border md:border-2 border-primary text-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-4 focus:ring-primary focus:ring-opacity-40', pulse_classes: 'ring-primary/50'},
  { name: 'primary-outline-white-text', classes: 'border md:border-2 border-primary text-white hover:bg-primary focus:outline-none focus:ring-4 focus:ring-primary focus:ring-opacity-40', pulse_classes: 'ring-primary/50'},
  { name: 'secondary', classes: 'border-none bg-secondary text-white hover:bg-dark-900 focus:outline-none focus:ring-4 focus:ring-secondary focus:ring-opacity-40', pulse_classes: 'ring-secondary/50' },
  { name: 'secondary-outline', classes: 'border md:border-2 border-secondary text-secondary hover:bg-secondary hover:text-white focus:outline-none focus:ring-4 focus:ring-secondary focus:ring-opacity-40', pulse_classes: 'ring-secondary/50' },
  { name: 'tertiary', classes: 'border-none bg-white text-secondary hover:bg-tertiary focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-40', pulse_classes: 'ring-tertiary/50' },
  { name: 'tertiary-outline', classes: 'border md:border-2 border-white text-white hover:bg-tertiary-light hover:text-secondary focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-40', pulse_classes: 'ring-tertiary/50' },
  { name: 'link', classes: 'currentColor underline focus:outline-none hover:font-bold', pulse_classes: '' },
  { name: 'facebook', classes: 'bg-[#4267B2] text-white hover:bg-opacity-95 focus:outline-none focus:ring-4 focus:ring-opacity-40', pulse_classes: 'ring-[#4267B2] ring-opacity-50' },
  { name: 'apple', classes: 'border-none bg-black text-white hover:bg-secondary-dark focus:outline-none focus:ring-4 focus:ring-secondary focus:ring-opacity-40', pulse_classes: 'ring-secondary/50' },
  { name: 'google', classes: 'border-none bg-white text-secondary hover:bg-tertiary focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-40', pulse_classes: 'ring-tertiary/50' },
]

const sizes = [
  { name: 'small', classes: 'font-medium px-3 py-1.5 text-[11px] rounded-md xl:rounded-lg' },
  { name: 'normal', classes: 'font-bold uppercase px-8 py-4 text-[13px] sm:text-[16px] sm:px-10 rounded-lg tracking-wide xl:py-5 leading-snug' },
  { name: 'large', classes: ' font-bold uppercase px-10 py-5 text-[15px] sm:text-[16px] sm:px-12 rounded-lg tracking-wide' }
]

const sizesCircle = [
  { name: 'small', classes: 'p-1 text-xs font-bold' },
  { name: 'normal', classes: 'p-2 text-sm font-bold' },
  { name: 'large', classes: 'p-2 text-base font-bold' }
]

const variantClasses = computed(() => {
  return variants.find(i => i.name === props.variant).classes
})

const sizeClasses = computed(() => {
  if (props.circle) {
    return sizesCircle.find(i => i.name === props.size).classes
  } else {
    return sizes.find(i => i.name === props.size).classes
  }
})

const pulseClasses = computed(() => {
  return variants.find(i => i.name === props.variant).pulse_classes
})

const classes = computed(() => {
  return `${variantClasses.value} ${sizeClasses.value}`
})

</script>

<style scoped>
@keyframes pulse{0%{transform:scale(1)}50%{transform:scale(1.02)}to{transform:scale(1)}}@keyframes pulse-ring{0%{opacity:.01}50%{opacity:1}to{opacity:.01}}.animate-pulse{animation:pulse 3s infinite}.animate-pulse-ring{animation:pulse-ring 3s infinite}
</style>
